import { useState, useCallback } from 'react';
import Row from '@sdk/components/ui/spacing/Row';
import gettext from '@sdk/lang/gettext';
import { useClickOutside } from '@sdk/common/hooks/useClickOutside';
import { Container, Menu, MenuOption, Text } from '@sdk/components/ui/moreOptionsMenu.styles';
import { AllBreakTypes, AutomatedBreakType } from '@sdk/settings/types';
import { BreakAttestation } from '@sdk/automatedBreaks/types/Attestation';

import AddEntryButton from '@web/timesheets/CreateTimesheetModal/components/buttons/AddEntryButton';
import useAutomatedBreaksEnabled from '@web/timeClock/hooks/useAutomatedBreaksEnabled';
import { useAutomatedBreaksSettings } from '@web/settings/components/schedule/Breaks/breaks.hooks';
import { BreakEntry } from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.types';

type Props = {
  timeClockEntryKey: string;
  addBreak: (
    timeClockEntryKey: string,
    breakType: AllBreakTypes,
    replacesAttestedBreakNumber?: number
  ) => void;
  entryHasAttestations: boolean;
  askForExcessiveBreaksConfirmation: () => Promise<boolean>;
  chooseAttestationToReplace: (arg: {
    breakType: AllBreakTypes;
    breakNumbers: number[];
  }) => Promise<number | boolean>;
  preventedAttestations: BreakAttestation[];
  existingBreaks: BreakEntry[];
};

const AddBreakButton = ({
  timeClockEntryKey,
  addBreak,
  entryHasAttestations,
  askForExcessiveBreaksConfirmation,
  chooseAttestationToReplace,
  preventedAttestations,
  existingBreaks,
}: Props) => {
  const areAutomatedBreaksUsed = useAutomatedBreaksEnabled();
  const { automatedBreaks } = useAutomatedBreaksSettings();
  const [isMenuOpened, setIsMenuOpened] = useState(false);

  const clickOutsideCallback = useCallback(() => {
    setIsMenuOpened(false);
  }, [setIsMenuOpened]);
  const ref = useClickOutside<HTMLDivElement>(clickOutsideCallback);

  if (!areAutomatedBreaksUsed) {
    return (
      <Row mTop={10} justifyContent="flex-end">
        <AddEntryButton
          label={gettext('Add break')}
          onClick={() => addBreak(timeClockEntryKey, AllBreakTypes.standard)}
        />
      </Row>
    );
  }

  const addMealBreak = async () => {
    const alreadyMappedNumbers = existingBreaks
      .filter(({ deleted }) => !deleted)
      .map(({ replacesAttestedBreakNumber }) => replacesAttestedBreakNumber);

    const preventedMealAttestations = preventedAttestations.filter(
      ({ breakType, breakNumber }) =>
        breakType === AutomatedBreakType.meal && !alreadyMappedNumbers.includes(breakNumber)
    );

    setIsMenuOpened(false);
    if (preventedMealAttestations.length) {
      const replacesAttestedBreakNumber = await chooseAttestationToReplace({
        breakType: AllBreakTypes.meal,
        breakNumbers: preventedMealAttestations.map((a) => a.breakNumber),
      });

      if (typeof replacesAttestedBreakNumber === 'number') {
        addBreak(timeClockEntryKey, AllBreakTypes.meal, replacesAttestedBreakNumber);
      }

      return;
    } else if (entryHasAttestations) {
      const areExcessiveBreaksAccepted = await askForExcessiveBreaksConfirmation();
      if (!areExcessiveBreaksAccepted) {
        return;
      }
    }

    addBreak(timeClockEntryKey, AllBreakTypes.meal);
  };

  const addRestBreak = async () => {
    const alreadyMappedNumbers = existingBreaks
      .filter(({ deleted }) => !deleted)
      .map(({ replacesAttestedBreakNumber }) => replacesAttestedBreakNumber);

    const preventedRestAttestations = preventedAttestations.filter(
      ({ breakType, breakNumber }) =>
        breakType === AutomatedBreakType.rest && !alreadyMappedNumbers.includes(breakNumber)
    );

    setIsMenuOpened(false);
    if (preventedRestAttestations.length) {
      const replacesAttestedBreakNumber = await chooseAttestationToReplace({
        breakType: AllBreakTypes.rest,
        breakNumbers: preventedRestAttestations.map((a) => a.breakNumber),
      });

      if (typeof replacesAttestedBreakNumber === 'number') {
        addBreak(timeClockEntryKey, AllBreakTypes.rest, replacesAttestedBreakNumber);
      }

      return;
    } else if (entryHasAttestations) {
      const areExcessiveBreaksAccepted = await askForExcessiveBreaksConfirmation();
      if (!areExcessiveBreaksAccepted) {
        return;
      }
    }
    addBreak(timeClockEntryKey, AllBreakTypes.rest);
  };

  return (
    <Row mTop={10} justifyContent="flex-end">
      <Container ref={ref}>
        <AddEntryButton label={gettext('Add break')} onClick={() => setIsMenuOpened(true)} />
        {isMenuOpened && (
          <Menu>
            {automatedBreaks.meal.isEnabled && (
              <MenuOption onClick={addMealBreak}>
                <Text>{gettext('Meal break')}</Text>
              </MenuOption>
            )}
            {automatedBreaks.rest.isEnabled && (
              <MenuOption onClick={addRestBreak}>
                <Text>{gettext('Rest break')}</Text>
              </MenuOption>
            )}
          </Menu>
        )}
      </Container>
    </Row>
  );
};

export default AddBreakButton;
